import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, MapPin, Users, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { euro } from "@/lib/format";
import { STATI, fmtDay } from "@/lib/cantieri";
import { OrePanel } from "@/components/cantieri/OrePanel";
import { AccontiPanel } from "@/components/cantieri/AccontiPanel";
import { SpeseCantierePanel } from "@/components/cantieri/SpeseCantierePanel";
import { BilancioCantiere } from "@/components/cantieri/BilancioCantiere";
import { FattureCollegate } from "@/components/cantieri/FattureCollegate";

const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-border bg-card p-3">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-heading text-lg font-bold mt-0.5" data-testid={testid}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function CantiereDetail({ id, operai, onClose, onEdit, onChanged }) {
  const [c, setC] = useState(null);
  useEffect(() => { setC(null); if (id) api.get(`/cantieri/${id}`).then((r) => setC(r.data)).catch(onClose); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async (next) => { setC(next || (await api.get(`/cantieri/${id}`)).data); onChanged(); };
  const setStato = async (stato) => { await api.patch(`/cantieri/${id}/stato`, { stato }); refresh(); };
  const names = operai.filter((o) => c?.operai_ids?.includes(o.id)).map((o) => o.nome).join(", ");
  const [askDel, setAskDel] = useState(false);
  const del = async () => {
    await api.delete(`/cantieri/${id}`);
    toast.success("Cantiere eliminato");
    setAskDel(false); onClose(); onChanged();
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="cantiere-detail">
        <DialogTitle className="font-heading text-xl pr-8">{c?.nome || "Cantiere"}</DialogTitle>
        <DialogDescription className="sr-only">Dettaglio cantiere</DialogDescription>
        {c && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {c.cliente_nome && <span className="font-medium text-foreground">{c.cliente_nome}</span>}
              {c.indirizzo && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.indirizzo}</span>}
              <span>{fmtDay(c.data_inizio)} → {fmtDay(c.data_fine)}</span>
              {c.quote_numero && <span>Prev. N. {c.quote_numero}</span>}
              {c.invoice_numero && <span>Fatt. N. {c.invoice_numero}</span>}
              <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap gap-2">
                <Select value={c.stato} onValueChange={setStato}>
                  <SelectTrigger className="w-36 h-9" data-testid="cantiere-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid="cantiere-detail-edit"><Pencil className="w-4 h-4 mr-1.5" /> Modifica</Button>
                <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setAskDel(true)} data-testid="cantiere-delete-button"><Trash2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Elimina</span></Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Kpi label="Giorni lav." value={c.giorni_lavorativi} sub="domeniche escluse" testid="kpi-cantiere-days" />
              {c.calcolo === "libero"
                ? <Kpi label="Operai" value={c.operai_presenti} sub="hanno lavorato qui" testid="kpi-cantiere-workers" />
                : <Kpi label="Operai" value={c.num_operai} sub={`${c.ore_giorno} h/giorno`} testid="kpi-cantiere-workers" />}
              <Kpi label={c.calcolo === "libero" ? "Ore stimate" : "Ore previste"} value={c.ore_previste ? c.ore_previste.toLocaleString("it-IT") : "—"} sub={c.calcolo === "libero" ? "giorno per giorno" : ""} testid="kpi-cantiere-planned" />
              <Kpi label="Ore lavorate" value={c.ore_lavorate?.toLocaleString("it-IT")} sub={c.ore_previste ? `${c.avanzamento}%` : ""} testid="kpi-cantiere-worked" />
              <Kpi label="Costo manodopera" value={euro(c.costo_manodopera)} testid="kpi-cantiere-cost" />
              <Kpi label="Incassato" value={euro(c.totale_incassato)} sub={c.residuo != null ? `Residuo ${euro(c.residuo)}` : ""} testid="kpi-cantiere-advances" />
            </div>
            <BilancioCantiere c={c} />
            {c.fatture_collegate?.length > 0 && (
              <div className="rounded-xl border border-border p-3 space-y-1.5">
                <div className="text-xs font-semibold text-muted-foreground">Pagamenti letti dalle fatture collegate{c.totale_acconti > 0 ? ` (+ ${euro(c.totale_acconti)} di acconti segnati qui)` : ""}</div>
                <FattureCollegate fatture={c.fatture_collegate} />
              </div>
            )}
            <Progress value={c.avanzamento} className="h-2" />
            {names && <div className="text-sm flex items-center gap-2 text-muted-foreground"><Users className="w-4 h-4" /> {names}</div>}
            <Tabs defaultValue="ore">
              <TabsList>
                <TabsTrigger value="ore" data-testid="cantiere-tab-hours">Ore lavorate</TabsTrigger>
                <TabsTrigger value="spese" data-testid="cantiere-tab-expenses">Spese</TabsTrigger>
                <TabsTrigger value="acconti" data-testid="cantiere-tab-advances">Acconti</TabsTrigger>
              </TabsList>
              <TabsContent value="ore" className="pt-3"><OrePanel cantiere={c} operai={operai} onChanged={refresh} /></TabsContent>
              <TabsContent value="spese" forceMount className="pt-3 data-[state=inactive]:hidden"><SpeseCantierePanel cantiere={c} onChanged={refresh} /></TabsContent>
              <TabsContent value="acconti" className="pt-3"><AccontiPanel cantiere={c} onChanged={refresh} /></TabsContent>
            </Tabs>
            {c.note && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{c.note}</p>}
          </div>
        )}
        <AlertDialog open={askDel} onOpenChange={setAskDel}>
          <AlertDialogContent data-testid="cantiere-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminare il cantiere «{c?.nome}»?</AlertDialogTitle>
              <AlertDialogDescription>Vengono cancellati anche le ore lavorate, le spese e gli acconti registrati. L'operazione non si può annullare.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="cantiere-delete-confirm">Elimina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
